import React, { useCallback, useMemo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import Select from 'components/v2/select/select';
import shopifyLogo from 'public/images/shopify-logo.png';
import cx from 'classnames';
import {
  ongoingBookingFilterSelector,
  ongoingDeliveriesV2Selector,
} from 'selectors/new-history-selectors';
import {
  NEW_ONGOING_ORDER_STATE_FILTER,
  ORDER_TYPES,
} from 'constants/order-constants';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import {
  SCRIBE_STATE_NAMES,
  SCRIBE_SHOPIFY_EVENTS,
} from 'applications/scribe/instrumentations/track';
import ScribeDuck from 'ducks/scribe-duck';
import TrackGADuck from 'ducks/track-ga-duck';
import NewHistoryDuck from 'ducks/new-history-duck';
import commonTable from './common-table.less';
import styles from './ongoing-booking-section.less';

const OngoingBookingFilter = () => {
  const dispatch = useDispatch();
  const shopifyTrackCtx = useContext(ShopifyTrackEventContext);

  const { orderState, orderType, pageSize } = useSelector(
    ongoingBookingFilterSelector,
  );
  const { loading } = useSelector(ongoingDeliveriesV2Selector);

  // order states
  const allOrderStates = useMemo(
    () =>
      Object.entries(NEW_ONGOING_ORDER_STATE_FILTER).map(([state, config]) => ({
        value: state,
        title: I18n.t(config.text),
        child: I18n.t(config.text),
      })),
    [],
  );
  const onChangeOrderState = useCallback(
    orderState => {
      dispatch(
        TrackGADuck.creators.trackGAExpressBookingsWebClick(
          'ONGOING_BOOKINGS_STATUS',
        ),
      );
      dispatch(
        ScribeDuck.creators.scribeExpressBookingsWebOngoingBookingsStatus(),
      );
      dispatch(
        NewHistoryDuck.creators.updateOngoingBookingFilter({ orderState }),
      );
    },
    [dispatch],
  );

  // order type
  const allOrderTypes = useMemo(
    () =>
      Object.keys(ORDER_TYPES)
        .map(ot => ({
          value: ot,
          title: I18n.t(`history.orderTypes.${ot.toLowerCase()}`),
        }))
        .map(opt => ({
          value: opt.value,
          title: opt.title,
          child: opt.title,
          icon: opt.value === ORDER_TYPES.SHOPIFY ? shopifyLogo : undefined,
        })),
    [],
  );
  const onChangeOrderType = useCallback(
    orderType => {
      if (orderType === ORDER_TYPES.SHOPIFY) {
        shopifyTrackCtx.fireShopifyTrackEvent({
          stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_SHOPIFY_ORDERS,
        });
      }

      dispatch(
        ScribeDuck.creators.scribeExpressBookingsWebOngoingBookingsFilter(
          I18n.t(`history.orderTypes.${orderType.toLowerCase()}`, {
            locale: 'en',
          }),
        ),
      );
      dispatch(
        NewHistoryDuck.creators.updateOngoingBookingFilter({ orderType }),
      );
    },
    [dispatch],
  );

  // pagination page size
  const allPageSize = useMemo(
    () => [
      {
        value: '5',
        title: I18n.t('v2.bookingsPage.paginationOption', { num: 5 }),
        child: I18n.t('v2.bookingsPage.paginationOption', { num: 5 }),
      },
      {
        value: '20',
        title: I18n.t('v2.bookingsPage.paginationOption', { num: 20 }),
        child: I18n.t('v2.bookingsPage.paginationOption', { num: 20 }),
      },
      {
        value: '50',
        title: I18n.t('v2.bookingsPage.paginationOption', { num: 50 }),
        child: I18n.t('v2.bookingsPage.paginationOption', { num: 50 }),
      },
    ],
    [],
  );
  const onChangePageSize = useCallback(
    pageSize => {
      dispatch(
        ScribeDuck.creators.scribeExpressBookingsWebShowOngoingBookings(
          pageSize,
        ),
      );
      dispatch(
        NewHistoryDuck.creators.updateOngoingBookingFilter({ pageSize }),
      );
    },
    [dispatch],
  );

  return (
    <div className={cx(commonTable.bookingFilter, styles.ongoingBookingFilter)}>
      <Select
        placeholder=""
        options={allOrderTypes}
        className={commonTable.select}
        optionClassName={commonTable.selectOption}
        value={orderType}
        onChange={onChangeOrderType}
        disabled={loading}
      />
      <Select
        options={allOrderStates}
        className={commonTable.select}
        optionClassName={commonTable.selectOption}
        value={orderState}
        onChange={onChangeOrderState}
        disabled={loading}
      />
      <Select
        options={allPageSize}
        className={cx(commonTable.select, styles.select)}
        optionClassName={commonTable.selectOption}
        value={pageSize}
        onChange={onChangePageSize}
        disabled={loading}
      />
    </div>
  );
};

export default OngoingBookingFilter;
